import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import MaterialTable from "material-table";
import moment from "moment-timezone";

import { EVNCOLS } from "../../../util/columns";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";
import { dirname } from "../../../util/resourcePath";

import LoaderGraph from "../../general/LoaderGraph";
import "react-datepicker/dist/react-datepicker.css";
import dividePages from "../../../util/dividePages";
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";

function InputEVN() {
	const evncols = useMemo(() => EVNCOLS, []); // Columns for table
	const [tableState, setTableState] = useState(false); // Table is ready or not
	const [data, setData] = useState([]); // Data for table
	const [title, setTitle] = useState(""); // Title of table
	const [datePicker, setDatePicker] = useState(new Date()); // Date of date-picker
	const [minDate, setMinDate] = useState(new Date()); // Min date of date-picker
	const [maxDate, setMaxDate] = useState(new Date()); // Max date of date-picker
	const user = useUser(); // Logged-in user

	const [noOfPages, setNoOfPages] = useState([1]);

	const [dateReady, setDateReady] = useState(false);
	const [tableLoading, setTableLoading] = useState(true);
	const directory = dirname();

	const make_sure_no_missing_sites = async (date) => {
		if (date === undefined) {
			date = datePicker;
		}
		await GYAxios.get(`/evn_input/missingSites?monthpicked=${moment(date).format("YYYYMMDD")}`);
	};

	/**
	 * Get list of sites that their previous-month invoices being approved
	 * @param {object} datePicked would be empty when first loading
	 * and would be updated when Users change month-year in date-picker
	 */
	async function extractData(_datePicked) {
		setTableLoading(true);
		let datePicked = moment(datePicker).format("YYYYMMDD");
		if (_datePicked !== undefined)
			datePicked = moment(_datePicked).format("YYYYMMDD");
		try {
			const res = await GYAxios.get(
				`/evn_input/EVNmonthlysites?monthpicked=${datePicked}`
			);

			let string = "EVN Invoice in ";
			const array = res.data.map((v) => {
				return {
					id: v.id,
					client: v.company,
					site: v.site_name,
					tableName: v.table_name,
					invMon: v.ref_monthData,
					startDate: v.start_date,
					endDate: v.end_date,
					totalDe: parseFloat(v.EVN_total).toFixed(3),
					onDe: parseFloat(v.EVN_on).toFixed(3),
					offDe: (parseFloat(v.EVN_off) - parseFloat(v.EVN_std_temp)).toFixed(3),
					stdDe: parseFloat(v.EVN_std_temp).toFixed(3),
					updatedID: v.last_updated_ID,
					updatedBy: v.last_updated_name,
					updatedAt: v.last_updated_at,
					approvalStatus: v.approvalStatus,
				};
			});

			string += String(moment(array[0].invMon).format("MMMM"));
		
			setData(array);
			setTitle(string);
			setTableState(true);
			setTableLoading(false);
		} catch (error) {
			Noti.create({
				type: "e",
				msg: "Something went wrong!",
			});
			console.log("error:", error);
		}
	}

	/**
	 * Validate the updated end date
	 * @param {Object} data merged data of old and new data of selected row
	 * @returns boolean
	 */
	function isEndDateValid(data) {
		let isValid = false;
		const today = moment().format("YYMMDD");
		const endDate_MM = moment(data.endDate, "YYMMDD").format("YYMM");
		const invMon_MM = moment(data.invMon).format("YYMM");
		const invMon_MM01 = moment(data.invMon).add(1, "M").format("YYMM");

		if (
			data.endDate <= today &&
			data.endDate > data.startDate &&
			(endDate_MM === invMon_MM || endDate_MM === invMon_MM01)
		)
			isValid = true;

		return isValid;
	}

	/**
	 * Validate the updated start date
	 * @param {Object} data merged data of old and new data of selected row
	 * @returns boolean
	 */
	function isStartDateValid(data) {
		let isValid = false;
		const today = moment().format("YYMMDD");
		const startDate_MM = moment(data.startDate, "YYMMDD").format("YYMM");
		const invMon_MM = moment(data.invMon).format("YYMM");
		const invMon_MM01 = moment(data.invMon).subtract(1, "M").format("YYMM");

		if (
			data.startDate <= today &&
			(startDate_MM === invMon_MM || startDate_MM === invMon_MM01)
		)
			isValid = true;
		return isValid;
	}

	/**
	 * Update data of selected row to server database
	 * and set data for table again (with updated values)
	 * @param {Object} newData updated data of selected row
	 * @param {Object} oldData old data of selected row
	 * @param {Function} resolve after Axios function
	 * -> reload the table value from front-end
	 */
	async function updateData(newData, oldData, resolve, reject) {
		let mistake = "";

		if (isNaN(newData?.onDe))
			mistake = "Peak Hour Delivered is invalid. Please check again.";
		if (isNaN(newData?.offDe))
			mistake = "Off Hour Delivered is invalid. Please check again.";
		if (isNaN(newData?.stdDe))
			mistake = "Standard Hour Delivered is invalid. Please check again.";
		const nV = {
			// nV = newValues
			startDate: moment(newData.startDate).format("YYMMDD"),
			endDate: moment(newData.endDate).format("YYMMDD"),
			totalDe: (parseFloat(newData.onDe) + parseFloat(newData.offDe) + parseFloat(newData.stdDe)).toFixed(3),
			onDe: parseFloat(newData.onDe).toFixed(3),
			offDe: parseFloat(newData.offDe).toFixed(3),
			stdDe: parseFloat(newData.stdDe).toFixed(3),
			updatedID: user.sub,
			updatedBy: user.email,
			invMon: oldData.invMon,
		};

		if (!isEndDateValid(nV))
			mistake = "End Date is invalid. Please check again.";
		if (!isStartDateValid(nV))
			mistake = "Start Date is invalid. Please check again.";

		if (mistake !== "") {
			Noti.create({ type: "e", msg: mistake });
			return reject();
		}

		await GYAxios.patch(
			`/evn_input/updateEVN?start=${nV.startDate}&end=${nV.endDate}&on=${nV.onDe}&off=${nV.offDe}&stdTemp=${nV.stdDe}&total=${nV.totalDe}&userid=${nV.updatedID}&email=${nV.updatedBy}&table=${oldData.tableName}&id=${oldData.id}`
		).then((res) => {
			if (res.data?.error !== undefined) {
				const bug = "Data is not updated. Please try again";
				Noti.create({ type: "e", msg: bug });
				reject();
			} else {
				const dataUpdate = [...data];
				const index = oldData.tableData.id;
				dataUpdate[index] = {
					...newData,
					updatedBy: nV.updatedBy,
					updatedAt: moment(),
					totalDe: nV.totalDe,
					onDe: nV.onDe,
					offDe: nV.offDe
				};
				setData([...dataUpdate]);
				setTimeout(() => {
					resolve();
				}, 500);
			}
		});
	}

	/**
	 * Update data of table and date selected of date-picker
	 * @param {Date} date that User changed
	 */
	function onChangeDatePicker(date) {
		setDatePicker(date);
		make_sure_no_missing_sites(date)
		extractData(date);
	}

	// Extract data list with the newest invoice period only once render
	useEffect(() => {
		const fetchDate = async () => {
			const response = await GYAxios.get("/evn_input/datePick").then((res) => {
				if (res.data?.error !== undefined) {
					console.log(res.data?.error);
					return null;
				}

				return res.data;
			});

			if (response?.length !== undefined && response?.length !== 0) {
				setDatePicker(moment(response[1][0].maxDate).toDate());
				setMinDate(moment(response[0][0].minDate).toDate());
				setMaxDate(moment(response[1][0].maxDate).toDate());
				setDateReady(true);
			}
		};		

		fetchDate();
	}, []);

	useEffect(() => {
		if (dateReady) {
			make_sure_no_missing_sites();
			extractData();
		}
	}, [dateReady]);

	useEffect(() => {
		dividePages(data.length, 20) === null
			? Noti.create({
				type: "e",
				msg: "Parameter for dividing pages is mismatch!",
			})
			: setNoOfPages(dividePages(data.length, 20));
	}, [data]);

	return (
		<>
			<Container fluid style={{ padding: 0 }}>
				<div className="panel-heading ">
					<Row>
						<Col md="6">
							<div className="panel-title" id="date">
								Monthly EVN Inputs
							</div>

							{dateReady && (
								<div className="month-selector">
									Choose another Month: &nbsp;&nbsp;
									<DatePicker
										minDate={localTimeToPickerTime(minDate)}
										maxDate={localTimeToPickerTime(maxDate)}
										selected={localTimeToPickerTime(datePicker)}
										onChange={(date) =>
											onChangeDatePicker(pickerTimeTolocalTime(date))
										}
										dateFormat="MMMM yyyy"
										showMonthYearPicker
									/>
								</div>
							)}
						</Col>
						<Col md="6" style={{ textAlign: "right" }}>
							<img
								src={`${directory}/images/reload.png`}
								onClick={() => { extractData(datePicker) }}
								style={{
									height: "35px",
									cursor: "pointer",
								}}
								alt="reload"
							/>
						</Col>
					</Row>
				</div>

				{tableState ? (
					<MaterialTable
						title={title}
						columns={evncols}
						data={data}
						isLoading={tableLoading}
						editable={{
							isEditable: (rowData) => rowData.approvalStatus < 2,
							onRowUpdate: (newData, oldData) =>
								new Promise((resolve, reject) => {
									updateData(newData, oldData, resolve, reject);
								}),
						}}
						options={{
							actionsColumnIndex: -1,
							sorting: true,
							pageSize: 20,
							pageSizeOptions: noOfPages,
							emptyRowsWhenPaging: false,
						}}
						style={{ marginBottom: "10px", zIndex: 0 }}
						detailPanel={(rowData) => {
							return (
								<ul
									style={{
										paddingLeft: "7%",
										margin: "10px",
										fontSize: "12px",
									}}
								>
									<li>Last updated by: {rowData.updatedBy}</li>
									<li>
										Last updated at:{" "}
										{moment(rowData.updatedAt).format("DD/MM/YYYY")}
									</li>
								</ul>
							);
						}}
					/>
				) : (
					<LoaderGraph />
				)}
			</Container>

			<Noti />
		</>
	);
}

export default InputEVN;
